import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { saFrequenciesData, type WebFrequency } from '@/mocks/superAdminFrequencies';
import FrecuenciaPreferidaPanel from './components/FrecuenciaPreferidaPanel';
import FrequencyFilters from './components/FrequencyFilters';
import FrequenciesTable from './components/FrequenciesTable';
import FrequencyActionModal from './components/FrequencyActionModal';
import AssignFrequencyModal from './components/AssignFrequencyModal';
import ReserveFrequencyModal from './components/ReserveFrequencyModal';
import FrequencyHistoryModal from './components/FrequencyHistoryModal';

export default function SuperAdminFrequencies() {
  const [frequencies, setFrequencies] = useState<WebFrequency[]>([]);
  const [activeFilter, setActiveFilter] = useState('todas');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Modals
  const [actionTarget, setActionTarget] = useState<WebFrequency | null>(null);
  const [assignTarget, setAssignTarget] = useState<WebFrequency | null>(null);
  const [reserveTarget, setReserveTarget] = useState<WebFrequency | null>(null);
  const [historyTarget, setHistoryTarget] = useState<WebFrequency | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(false);
    const timer = setTimeout(() => {
      if (Math.random() < 0.05) {
        setError(true);
        setLoading(false);
      } else {
        setFrequencies(saFrequenciesData);
        setLoading(false);
      }
    }, 700);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setLoading(true);
    setError(false);
    setTimeout(() => {
      if (Math.random() < 0.05) {
        setError(true);
        setLoading(false);
      } else {
        setFrequencies(saFrequenciesData);
        setLoading(false);
      }
    }, 700);
  }, []);

  const counts = useMemo(() => {
    const data = saFrequenciesData;
    return {
      todas: data.length,
      libres: data.filter((f) => f.status === 'libre').length,
      asignadas: data.filter((f) => f.status === 'asignada').length,
      reservadas: data.filter((f) => f.status === 'reservada').length,
      bloqueadas: data.filter((f) => f.status === 'bloqueada').length,
    };
  }, []);

  const filteredFrequencies = useMemo(() => {
    let result = frequencies;
    if (activeFilter === 'libres') result = result.filter((f) => f.status === 'libre');
    if (activeFilter === 'asignadas') result = result.filter((f) => f.status === 'asignada');
    if (activeFilter === 'reservadas') result = result.filter((f) => f.status === 'reservada');
    if (activeFilter === 'bloqueadas') result = result.filter((f) => f.status === 'bloqueada');
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (f) =>
          f.frequency.includes(q) ||
          (f.assignedRadio && f.assignedRadio.toLowerCase().includes(q)) ||
          (f.reservedFor && f.reservedFor.toLowerCase().includes(q)),
      );
    }
    return result;
  }, [frequencies, activeFilter, searchTerm]);

  // ─── Actions (mock state changes) ───
  const handleAssign = useCallback((freq: WebFrequency) => {
    setFrequencies((prev) =>
      prev.map((f) =>
        f.id === freq.id
          ? { ...f, status: 'asignada' as const, assignedRadio: 'Radio Asignada', assignedRadioId: 999, assignedAt: new Date().toISOString().split('T')[0], reservedFor: null, reservedAt: null, expiresAt: null }
          : f,
      ),
    );
  }, []);

  const handleReserve = useCallback((freq: WebFrequency, reservedFor: string | null) => {
    setFrequencies((prev) =>
      prev.map((f) =>
        f.id === freq.id
          ? { ...f, status: 'reservada' as const, reservedFor: reservedFor || 'Reserva administrativa', reservedAt: new Date().toISOString().split('T')[0], expiresAt: '2026-08-01T23:59:59' }
          : f,
      ),
    );
  }, []);

  const handleBlock = useCallback((freq: WebFrequency) => {
    setFrequencies((prev) =>
      prev.map((f) =>
        f.id === freq.id
          ? { ...f, status: 'bloqueada' as const, blockedAt: new Date().toISOString().split('T')[0], blockedReason: 'Bloqueo administrativo', assignedRadio: null, assignedRadioId: null, assignedAt: null, reservedFor: null, reservedAt: null, expiresAt: null }
          : f,
      ),
    );
  }, []);

  const handleUnblock = useCallback((freq: WebFrequency) => {
    setFrequencies((prev) =>
      prev.map((f) =>
        f.id === freq.id
          ? { ...f, status: 'libre' as const, blockedAt: null, blockedReason: null }
          : f,
      ),
    );
  }, []);

  const handleRelease = useCallback((freq: WebFrequency) => {
    setFrequencies((prev) =>
      prev.map((f) =>
        f.id === freq.id
          ? { ...f, status: 'libre' as const, assignedRadio: null, assignedRadioId: null, assignedAt: null, reservedFor: null, reservedAt: null, expiresAt: null }
          : f,
      ),
    );
  }, []);

  const kpiCards = [
    { key: 'libres', label: 'Libres', value: counts.libres, icon: 'ri-checkbox-blank-circle-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { key: 'asignadas', label: 'Asignadas', value: counts.asignadas, icon: 'ri-link', color: 'text-primary-600', bg: 'bg-primary-100' },
    { key: 'reservadas', label: 'Reservadas', value: counts.reservadas, icon: 'ri-bookmark-line', color: 'text-amber-600', bg: 'bg-amber-50' },
    { key: 'bloqueadas', label: 'Bloqueadas', value: counts.bloqueadas, icon: 'ri-forbid-line', color: 'text-red-500', bg: 'bg-red-50' },
  ];

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-4 max-w-[1600px] mx-auto">

            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Frecuencias</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Gestión de Frecuencias</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  87.00 – 200.00 RFW · {counts.todas.toLocaleString()} frecuencias · Incremento 0.01
                </p>
              </div>
              <div className="relative w-full sm:w-64">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar frecuencia o radio..."
                  className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
                  >
                    <i className="ri-close-line text-foreground-500 text-[10px]"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Frecuencia Preferida Pipeline */}
            <FrecuenciaPreferidaPanel
              frequencies={frequencies}
              onFrequencyAssigned={(numericValue: number, radioName: string) => {
                setFrequencies((prev) =>
                  prev.map((f) =>
                    Math.abs(f.numericValue - numericValue) < 0.001
                      ? { ...f, status: 'asignada' as const, assignedRadio: radioName, assignedRadioId: 999, assignedAt: new Date().toISOString().split('T')[0], reservedFor: null, reservedAt: null, expiresAt: null }
                      : f,
                  ),
                );
              }}
            />

            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {kpiCards.map((card) => (
                <button
                  key={card.key}
                  onClick={() => {
                    const filterMap: Record<string, string> = { libres: 'libres', asignadas: 'asignadas', reservadas: 'reservadas', bloqueadas: 'bloqueadas' };
                    setActiveFilter(activeFilter === filterMap[card.key] ? 'todas' : filterMap[card.key]);
                  }}
                  className={`bg-background-50 border rounded-xl p-3 flex items-center gap-3 cursor-pointer transition-all hover:shadow-sm text-left ${
                    (activeFilter === 'libres' && card.key === 'libres') ||
                    (activeFilter === 'asignadas' && card.key === 'asignadas') ||
                    (activeFilter === 'reservadas' && card.key === 'reservadas') ||
                    (activeFilter === 'bloqueadas' && card.key === 'bloqueadas')
                      ? 'border-foreground-300'
                      : 'border-background-200 hover:border-background-300'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center shrink-0`}>
                    <i className={`${card.icon} ${card.color} text-sm`}></i>
                  </div>
                  <div>
                    <p className="text-foreground-400 text-[10px] font-medium">{card.label}</p>
                    <p className="text-foreground-800 text-base font-bold">{card.value.toLocaleString()}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Filters */}
            <FrequencyFilters
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              counts={counts}
            />

            {/* Table */}
            <FrequenciesTable
              frequencies={filteredFrequencies}
              onOpenAction={setActionTarget}
              loading={loading}
              error={error}
              onRetry={handleRetry}
            />

          </div>
        </main>
      </div>

      {/* Modals */}
      <FrequencyActionModal
        frequency={actionTarget}
        onClose={() => setActionTarget(null)}
        onAssign={(f) => { setAssignTarget(f); }}
        onReserve={(f) => { setReserveTarget(f); }}
        onBlock={handleBlock}
        onViewHistory={(f) => { setHistoryTarget(f); }}
        onUnblock={handleUnblock}
        onRelease={handleRelease}
      />

      <AssignFrequencyModal
        frequency={assignTarget}
        onClose={() => setAssignTarget(null)}
        onConfirm={(freq, _radioId, _radioName) => { handleAssign(freq); setAssignTarget(null); }}
      />

      <ReserveFrequencyModal
        frequency={reserveTarget}
        onClose={() => setReserveTarget(null)}
        onConfirm={(freq, reservedFor) => { handleReserve(freq, reservedFor); setReserveTarget(null); }}
      />

      <FrequencyHistoryModal
        frequency={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">
          Super Admin · Gestión de Frecuencias
        </p>
      </div>
    </div>
  );
}